import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/lib/i18n';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Slider } from '@/components/ui/slider';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowRight, ArrowLeft, Check, Sparkles, Heart, Shield, Pill } from 'lucide-react';
import { API } from '@/lib/api';



export default function OnboardingPage() {
  const { language } = useLanguage();
  const { getHeaders, updateUser } = useAuth();
  const nav = useNavigate();
  const de = language === 'de';
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [glowResult, setGlowResult] = useState(null);
  const [data, setData] = useState({
    goals: [], age_range: '', current_weight: '', goal_weight: '', height_cm: '',
    current_problems: [], activity_level: '',
    cycle_status: '', hot_flash_frequency: '', thyroid_status: '', tsh_value: '',
    sleep_quality: '', health_topics: [],
    medications: [], medical_conditions: [], injuries: [],
    meal_habits: [], cravings: '', skin_hair: [], joint_status: [],
    diet_preferences: [], cooking_frequency: '', allergies: [],
    energy_level: 5, stress_level: 5,
  });

  const STEPS = de
    ? ['Ziele', 'Hormone', 'Medikamente', 'Lebensstil', 'Ernährung']
    : ['Goals', 'Hormones', 'Medications', 'Lifestyle', 'Nutrition'];
  const progress = ((step + 1) / STEPS.length) * 100;

  const toggle = (key, val) => {
    setData(prev => ({
      ...prev,
      [key]: prev[key].includes(val) ? prev[key].filter(v => v !== val) : [...prev[key], val]
    }));
  };

  const Chip = ({ items, selected, onToggle, testPrefix }) => (
    <div className="flex flex-wrap gap-2">
      {items.map(item => (
        <button key={item.key} onClick={() => onToggle(item.key)}
          className={`px-4 py-2 rounded-full font-body text-sm font-medium transition-all border ${selected.includes(item.key) ? 'bg-terracotta text-white border-terracotta' : 'bg-white text-foreground/70 border-border/80 hover:border-terracotta/50'}`}
          data-testid={`${testPrefix}-${item.key}`}>
          {item.label}
        </button>
      ))}
    </div>
  );

  const Radio = ({ value, options, onChange, testPrefix }) => (
    <div className="flex flex-wrap gap-2">
      {options.map(opt => (
        <button key={opt.key} onClick={() => onChange(opt.key)}
          className={`px-4 py-2 rounded-full font-body text-sm font-medium transition-all border ${value === opt.key ? 'bg-sage text-white border-sage' : 'bg-white text-foreground/70 border-border/80 hover:border-sage/50'}`}
          data-testid={`${testPrefix}-${opt.key}`}>
          {opt.label}
        </button>
      ))}
    </div>
  );

  const addMedication = (name) => {
    if (!data.medications.find(m => m.name === name)) {
      setData(prev => ({ ...prev, medications: [...prev.medications, { name, dose: '', timing: '' }] }));
    } else {
      setData(prev => ({ ...prev, medications: prev.medications.filter(m => m.name !== name) }));
    }
  };

  const handleFinish = async () => {
    setLoading(true);
    try {
      const payload = {
        ...data,
        current_weight: data.current_weight ? parseFloat(data.current_weight) : null,
        goal_weight: data.goal_weight ? parseFloat(data.goal_weight) : null,
        height_cm: data.height_cm ? parseInt(data.height_cm) : null,
        tsh_value: data.tsh_value ? parseFloat(data.tsh_value) : null,
        energy_level: data.energy_level,
        stress_level: data.stress_level,
      };
      const res = await axios.post(`${API}/onboarding`, payload, { headers: getHeaders() });
      updateUser({ onboarding_completed: true });
      setGlowResult(res.data.initial_glow_score || 68);
      setStep(STEPS.length);
    } catch {
      toast.error(de ? 'Fehler beim Speichern' : 'Error saving');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-bone flex items-center justify-center p-4 md:p-6" data-testid="onboarding-page">
      <div className="w-full max-w-2xl">
        <div className="text-center mb-6">
          <span className="font-display text-2xl font-semibold"><span className="text-terracotta">bel</span>osophie</span>
          {step < STEPS.length && (
            <>
              <h1 className="font-display text-2xl md:text-3xl font-medium mt-3">{de ? 'Dein GLOEW-Startprofil' : 'Your GLOW Start Profile'}</h1>
              <p className="font-body text-foreground/50 mt-1 text-sm">{de ? '2 Minuten – für dein personalisiertes Coaching' : '2 minutes – for your personalized coaching'}</p>
            </>
          )}
        </div>

        {step < STEPS.length && (
          <div className="mb-6">
            <div className="flex justify-between mb-2">
              {STEPS.map((s, i) => (
                <span key={i} className={`font-body text-[10px] md:text-xs font-medium ${i <= step ? 'text-terracotta' : 'text-foreground/30'}`}>{s}</span>
              ))}
            </div>
            <Progress value={progress} className="h-2 bg-warmgray [&>div]:bg-terracotta" data-testid="onboarding-progress" />
          </div>
        )}

        {/* STEP 0: Ziele & Körper */}
        {step === 0 && (
          <Card className="bg-white border border-border/60 rounded-2xl p-6 md:p-8 shadow-sm animate-fade-in" data-testid="step-goals">
            <div className="flex items-center gap-2 mb-5">
              <Heart size={20} className="text-terracotta" />
              <h2 className="font-display text-xl font-medium">{de ? 'Körper & Ziele' : 'Body & Goals'}</h2>
            </div>
            <div className="space-y-5">
              <div>
                <Label className="font-body text-sm text-foreground/70 mb-2 block">{de ? 'Dein Alter?' : 'Your age?'}</Label>
                <Radio value={data.age_range} testPrefix="age" onChange={v => setData({...data, age_range: v})} options={[
                  { key: '40-45', label: '40-45' }, { key: '46-50', label: '46-50' },
                  { key: '51-55', label: '51-55' }, { key: '56-60', label: '56-60' }, { key: '60+', label: '60+' },
                ]} />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="font-body text-xs text-foreground/50">{de ? 'Grösse (cm)' : 'Height (cm)'}</Label>
                  <Input type="number" value={data.height_cm} onChange={e => setData({...data, height_cm: e.target.value})} className="mt-1 rounded-xl" data-testid="input-height" />
                </div>
                <div>
                  <Label className="font-body text-xs text-foreground/50">{de ? 'Gewicht (kg)' : 'Weight (kg)'}</Label>
                  <Input type="number" value={data.current_weight} onChange={e => setData({...data, current_weight: e.target.value})} className="mt-1 rounded-xl" data-testid="input-weight" />
                </div>
                <div>
                  <Label className="font-body text-xs text-foreground/50">{de ? 'Zielgewicht' : 'Goal weight'}</Label>
                  <Input type="number" value={data.goal_weight} onChange={e => setData({...data, goal_weight: e.target.value})} className="mt-1 rounded-xl" data-testid="input-goal-weight" />
                </div>
              </div>
              <div>
                <Label className="font-body text-sm text-foreground/70 mb-2 block">{de ? 'Hauptziel?' : 'Main goal?'}</Label>
                <Chip selected={data.goals} testPrefix="goal" onToggle={v => toggle('goals', v)} items={de ? [
                  { key: 'weight', label: '5-10kg weniger' }, { key: 'energy', label: 'Mehr Energie' },
                  { key: 'glow', label: 'Bessere Haut / Glow' }, { key: 'clothes', label: 'Kleidergrösse halten' },
                  { key: 'muscle', label: 'Muskelerhalt' }, { key: 'stress', label: 'Weniger Stress-Essen' },
                ] : [
                  { key: 'weight', label: 'Lose 5-10kg' }, { key: 'energy', label: 'More energy' },
                  { key: 'glow', label: 'Better skin / glow' }, { key: 'clothes', label: 'Keep clothing size' },
                  { key: 'muscle', label: 'Muscle retention' }, { key: 'stress', label: 'Less stress eating' },
                ]} />
              </div>
              <div>
                <Label className="font-body text-sm text-foreground/70 mb-2 block">{de ? 'Aktuelles Problem?' : 'Current problem?'}</Label>
                <Chip selected={data.current_problems} testPrefix="problem" onToggle={v => toggle('current_problems', v)} items={de ? [
                  { key: 'cravings', label: 'Heisshunger (abends)' }, { key: 'fatigue', label: 'Müdigkeit' },
                  { key: 'sleep', label: 'Schlafprobleme' }, { key: 'mood', label: 'Stimmungsschwankungen' },
                ] : [
                  { key: 'cravings', label: 'Cravings (evenings)' }, { key: 'fatigue', label: 'Fatigue' },
                  { key: 'sleep', label: 'Sleep issues' }, { key: 'mood', label: 'Mood swings' },
                ]} />
              </div>
            </div>
          </Card>
        )}

        {/* STEP 1: Hormone & Zyklus */}
        {step === 1 && (
          <Card className="bg-white border border-border/60 rounded-2xl p-6 md:p-8 shadow-sm animate-fade-in" data-testid="step-hormones">
            <div className="flex items-center gap-2 mb-5">
              <Sparkles size={20} className="text-terracotta" />
              <h2 className="font-display text-xl font-medium">{de ? 'Hormone & Zyklus' : 'Hormones & Cycle'}</h2>
            </div>
            <div className="space-y-5">
              <div>
                <Label className="font-body text-sm text-foreground/70 mb-2 block">{de ? 'Zyklus-Status?' : 'Cycle status?'}</Label>
                <Radio value={data.cycle_status} testPrefix="cycle" onChange={v => setData({...data, cycle_status: v})} options={de ? [
                  { key: 'regular', label: 'Regelmässig (21-35 Tage)' },
                  { key: 'irregular', label: 'Unregelmässig' },
                  { key: 'menopause', label: 'Wechseljahre (>12 Mon.)' },
                  { key: 'unsure', label: 'Unsicher' },
                ] : [
                  { key: 'regular', label: 'Regular (21-35 days)' },
                  { key: 'irregular', label: 'Irregular' },
                  { key: 'menopause', label: 'Menopause (>12 months)' },
                  { key: 'unsure', label: 'Unsure' },
                ]} />
              </div>
              <div>
                <Label className="font-body text-sm text-foreground/70 mb-2 block">{de ? 'Hitzewallungen?' : 'Hot flashes?'}</Label>
                <Radio value={data.hot_flash_frequency} testPrefix="hotflash" onChange={v => setData({...data, hot_flash_frequency: v})} options={de ? [
                  { key: 'never', label: 'Nie' }, { key: 'weekly', label: '1-3x/Woche' }, { key: 'daily', label: 'Täglich' },
                ] : [
                  { key: 'never', label: 'Never' }, { key: 'weekly', label: '1-3x/week' }, { key: 'daily', label: 'Daily' },
                ]} />
              </div>
              <div>
                <Label className="font-body text-sm text-foreground/70 mb-2 block">{de ? 'Schilddrüse?' : 'Thyroid?'}</Label>
                <Radio value={data.thyroid_status} testPrefix="thyroid" onChange={v => setData({...data, thyroid_status: v})} options={de ? [
                  { key: 'none', label: 'Keine Probleme' }, { key: 'hashimoto', label: 'Hashimoto' }, { key: 'other', label: 'Andere' },
                ] : [
                  { key: 'none', label: 'No issues' }, { key: 'hashimoto', label: 'Hashimoto' }, { key: 'other', label: 'Other' },
                ]} />
                {(data.thyroid_status === 'hashimoto' || data.thyroid_status === 'other') && (
                  <div className="mt-3">
                    <Label className="font-body text-xs text-foreground/50">{de ? 'TSH-Wert (falls bekannt)' : 'TSH value (if known)'}</Label>
                    <Input type="number" step="0.1" value={data.tsh_value} onChange={e => setData({...data, tsh_value: e.target.value})} placeholder="z.B. 2.5" className="mt-1 rounded-xl w-32" data-testid="input-tsh" />
                  </div>
                )}
              </div>
              <div>
                <Label className="font-body text-sm text-foreground/70 mb-2 block">{de ? 'Schlafqualität?' : 'Sleep quality?'}</Label>
                <Radio value={data.sleep_quality} testPrefix="sleep" onChange={v => setData({...data, sleep_quality: v})} options={de ? [
                  { key: 'good', label: 'Gut (6-8h)' }, { key: 'bad', label: 'Schlecht (oft wach)' }, { key: 'very_bad', label: 'Sehr schlecht' },
                ] : [
                  { key: 'good', label: 'Good (6-8h)' }, { key: 'bad', label: 'Bad (often awake)' }, { key: 'very_bad', label: 'Very bad' },
                ]} />
              </div>
            </div>
          </Card>
        )}

        {/* STEP 2: Medikamente */}
        {step === 2 && (
          <Card className="bg-white border border-border/60 rounded-2xl p-6 md:p-8 shadow-sm animate-fade-in" data-testid="step-medications">
            <div className="flex items-center gap-2 mb-5">
              <Pill size={20} className="text-terracotta" />
              <h2 className="font-display text-xl font-medium">{de ? 'Medikamente & Diagnosen' : 'Medications & Diagnoses'}</h2>
            </div>
            <div className="space-y-5">
              <div>
                <Label className="font-body text-sm text-foreground/70 mb-2 block">{de ? 'Aktuelle Medikamente?' : 'Current medications?'}</Label>
                <div className="flex flex-wrap gap-2">
                  {(de ? [
                    { key: 'levothyroxin', label: 'Levothyroxin (Schilddrüse)' },
                    { key: 'ozempic', label: 'Ozempic / Wegovy (GLP-1)' },
                    { key: 'mounjaro', label: 'Mounjaro (GLP-1)' },
                    { key: 'antidepressant', label: 'Antidepressiva' },
                    { key: 'blood_pressure', label: 'Blutdruck' },
                    { key: 'pantoprazol', label: 'Pantoprazol (Magen)' },
                    { key: 'estrogen', label: 'Östrogen/HRT' },
                    { key: 'none', label: 'Keine' },
                  ] : [
                    { key: 'levothyroxin', label: 'Levothyroxine (thyroid)' },
                    { key: 'ozempic', label: 'Ozempic / Wegovy (GLP-1)' },
                    { key: 'mounjaro', label: 'Mounjaro (GLP-1)' },
                    { key: 'antidepressant', label: 'Antidepressants' },
                    { key: 'blood_pressure', label: 'Blood pressure' },
                    { key: 'pantoprazol', label: 'Pantoprazole (stomach)' },
                    { key: 'estrogen', label: 'Estrogen/HRT' },
                    { key: 'none', label: 'None' },
                  ]).map(med => (
                    <button key={med.key} onClick={() => addMedication(med.key)}
                      className={`px-4 py-2 rounded-full font-body text-sm font-medium transition-all border ${data.medications.find(m => m.name === med.key) ? 'bg-terracotta text-white border-terracotta' : 'bg-white text-foreground/70 border-border/80 hover:border-terracotta/50'}`}
                      data-testid={`med-${med.key}`}>
                      {med.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="font-body text-sm text-foreground/70 mb-2 block">{de ? 'Ärztliche Diagnosen?' : 'Medical diagnoses?'}</Label>
                <Chip selected={data.medical_conditions} testPrefix="condition" onToggle={v => toggle('medical_conditions', v)} items={de ? [
                  { key: 'diabetes2', label: 'Diabetes Typ 2 / Prädiabetes' },
                  { key: 'fatty_liver', label: 'Fettleber' },
                  { key: 'pcos', label: 'PCOS' },
                  { key: 'osteoporosis', label: 'Osteoporose' },
                  { key: 'none', label: 'Keine' },
                ] : [
                  { key: 'diabetes2', label: 'Type 2 Diabetes / Prediabetes' },
                  { key: 'fatty_liver', label: 'Fatty liver' },
                  { key: 'pcos', label: 'PCOS' },
                  { key: 'osteoporosis', label: 'Osteoporosis' },
                  { key: 'none', label: 'None' },
                ]} />
              </div>
              <div>
                <Label className="font-body text-sm text-foreground/70 mb-2 block">{de ? 'Verletzungen?' : 'Injuries?'}</Label>
                <Chip selected={data.injuries} testPrefix="injury" onToggle={v => toggle('injuries', v)} items={de ? [
                  { key: 'knee_hip', label: 'Knie/Hüfte' }, { key: 'back', label: 'Rücken' },
                  { key: 'shoulder', label: 'Schulter' }, { key: 'none', label: 'Keine' },
                ] : [
                  { key: 'knee_hip', label: 'Knee/Hip' }, { key: 'back', label: 'Back' },
                  { key: 'shoulder', label: 'Shoulder' }, { key: 'none', label: 'None' },
                ]} />
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                <p className="font-body text-xs text-amber-700">
                  <Shield size={14} className="inline mr-1" />
                  {de ? 'belosophie ersetzt keine ärztliche Beratung. Konsultiere immer deine Ärztin/Endokrinologin.' : 'belosophie does not replace medical advice. Always consult your doctor/endocrinologist.'}
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* STEP 3: Lebensstil & Symptome */}
        {step === 3 && (
          <Card className="bg-white border border-border/60 rounded-2xl p-6 md:p-8 shadow-sm animate-fade-in" data-testid="step-lifestyle">
            <h2 className="font-display text-xl font-medium mb-5">{de ? 'Lebensstil & Symptome' : 'Lifestyle & Symptoms'}</h2>
            <div className="space-y-5">
              <div>
                <Label className="font-body text-sm text-foreground/70 mb-2 block">{de ? 'Haut & Haare?' : 'Skin & Hair?'}</Label>
                <Chip selected={data.skin_hair} testPrefix="skin" onToggle={v => toggle('skin_hair', v)} items={de ? [
                  { key: 'good_skin', label: 'Glatte Haut, glaenzende Haare' },
                  { key: 'dry_skin', label: 'Trockene Haut / Haarausfall' },
                  { key: 'pigment', label: 'Pigmentflecken / Tränensäcke' },
                ] : [
                  { key: 'good_skin', label: 'Smooth skin, shiny hair' },
                  { key: 'dry_skin', label: 'Dry skin / hair loss' },
                  { key: 'pigment', label: 'Pigment spots / dark circles' },
                ]} />
              </div>
              <div>
                <Label className="font-body text-sm text-foreground/70 mb-2 block">{de ? 'Gelenke / Muskeln?' : 'Joints / Muscles?'}</Label>
                <Chip selected={data.joint_status} testPrefix="joint" onToggle={v => toggle('joint_status', v)} items={de ? [
                  { key: 'flexible', label: 'Beweglich & schmerzfrei' },
                  { key: 'back_pain', label: 'Rückenschmerzen' },
                  { key: 'stiff', label: 'Gelenksteifigkeit' },
                ] : [
                  { key: 'flexible', label: 'Flexible & pain-free' },
                  { key: 'back_pain', label: 'Back pain' },
                  { key: 'stiff', label: 'Joint stiffness' },
                ]} />
              </div>
              <div>
                <Label className="font-body text-sm text-foreground/70 mb-1 block">
                  {de ? `Energiepegel: ${data.energy_level}/10` : `Energy level: ${data.energy_level}/10`}
                </Label>
                <Slider value={[data.energy_level]} min={1} max={10} step={1} onValueChange={v => setData({...data, energy_level: v[0]})} className="[&>span>span]:bg-terracotta" data-testid="slider-energy" />
              </div>
              <div>
                <Label className="font-body text-sm text-foreground/70 mb-1 block">
                  {de ? `Stresslevel: ${data.stress_level}/10` : `Stress level: ${data.stress_level}/10`}
                </Label>
                <Slider value={[data.stress_level]} min={1} max={10} step={1} onValueChange={v => setData({...data, stress_level: v[0]})} className="[&>span>span]:bg-terracotta" data-testid="slider-stress" />
              </div>
              <div>
                <Label className="font-body text-sm text-foreground/70 mb-2 block">{de ? 'Aktivitätslevel?' : 'Activity level?'}</Label>
                <Radio value={data.activity_level} testPrefix="activity" onChange={v => setData({...data, activity_level: v})} options={de ? [
                  { key: 'low', label: 'Wenig aktiv' }, { key: 'moderate', label: 'Mässig' },
                  { key: 'active', label: 'Aktiv' }, { key: 'very_active', label: 'Sehr aktiv' },
                ] : [
                  { key: 'low', label: 'Low' }, { key: 'moderate', label: 'Moderate' },
                  { key: 'active', label: 'Active' }, { key: 'very_active', label: 'Very active' },
                ]} />
              </div>
            </div>
          </Card>
        )}

        {/* STEP 4: Ernährung */}
        {step === 4 && (
          <Card className="bg-white border border-border/60 rounded-2xl p-6 md:p-8 shadow-sm animate-fade-in" data-testid="step-nutrition">
            <h2 className="font-display text-xl font-medium mb-5">{de ? 'Ernährung & Mahlzeiten' : 'Nutrition & Meals'}</h2>
            <div className="space-y-5">
              <div>
                <Label className="font-body text-sm text-foreground/70 mb-2 block">{de ? 'Typische Mahlzeiten?' : 'Typical meals?'}</Label>
                <Chip selected={data.meal_habits} testPrefix="meal" onToggle={v => toggle('meal_habits', v)} items={de ? [
                  { key: 'skip_breakfast', label: 'Fruehstück auslassen' },
                  { key: 'lunch_out', label: 'Mittagessen auswaerts' },
                  { key: 'late_dinner', label: 'Abendessen nach 20 Uhr' },
                  { key: 'snacking', label: 'Viel Snacking' },
                ] : [
                  { key: 'skip_breakfast', label: 'Skip breakfast' },
                  { key: 'lunch_out', label: 'Lunch out' },
                  { key: 'late_dinner', label: 'Dinner after 8pm' },
                  { key: 'snacking', label: 'Lots of snacking' },
                ]} />
              </div>
              <div>
                <Label className="font-body text-sm text-foreground/70 mb-2 block">{de ? 'Heisshunger?' : 'Cravings?'}</Label>
                <Radio value={data.cravings} testPrefix="cravings" onChange={v => setData({...data, cravings: v})} options={de ? [
                  { key: 'never', label: 'Nie' }, { key: 'afternoon', label: 'Nachmittags' }, { key: 'evening', label: 'Abends' },
                ] : [
                  { key: 'never', label: 'Never' }, { key: 'afternoon', label: 'Afternoon' }, { key: 'evening', label: 'Evening' },
                ]} />
              </div>
              <div>
                <Label className="font-body text-sm text-foreground/70 mb-2 block">{de ? 'Ernährungspräferenzen?' : 'Diet preferences?'}</Label>
                <Chip selected={data.diet_preferences} testPrefix="diet" onToggle={v => toggle('diet_preferences', v)} items={de ? [
                  { key: 'mixed', label: 'Mischkost' }, { key: 'vegetarian', label: 'Vegetarisch' },
                  { key: 'vegan', label: 'Vegan' }, { key: 'lowcarb', label: 'Low Carb' },
                  { key: 'mediterranean', label: 'Mediterran' },
                ] : [
                  { key: 'mixed', label: 'Mixed' }, { key: 'vegetarian', label: 'Vegetarian' },
                  { key: 'vegan', label: 'Vegan' }, { key: 'lowcarb', label: 'Low Carb' },
                  { key: 'mediterranean', label: 'Mediterranean' },
                ]} />
              </div>
              <div>
                <Label className="font-body text-sm text-foreground/70 mb-2 block">{de ? 'Wie oft kochst du selbst?' : 'How often do you cook?'}</Label>
                <Radio value={data.cooking_frequency} testPrefix="cooking" onChange={v => setData({...data, cooking_frequency: v})} options={de ? [
                  { key: 'daily', label: 'Täglich' }, { key: 'often', label: '4-5x/Woche' },
                  { key: 'sometimes', label: '2-3x/Woche' }, { key: 'rarely', label: 'Selten' },
                ] : [
                  { key: 'daily', label: 'Daily' }, { key: 'often', label: '4-5x/week' },
                  { key: 'sometimes', label: '2-3x/week' }, { key: 'rarely', label: 'Rarely' },
                ]} />
              </div>
              <div>
                <Label className="font-body text-sm text-foreground/70 mb-2 block">{de ? 'Allergien?' : 'Allergies?'}</Label>
                <Chip selected={data.allergies} testPrefix="allergy" onToggle={v => toggle('allergies', v)} items={de ? [
                  { key: 'lactose', label: 'Laktose' }, { key: 'gluten', label: 'Gluten' },
                  { key: 'nuts', label: 'Nüsse' }, { key: 'none', label: 'Keine' },
                ] : [
                  { key: 'lactose', label: 'Lactose' }, { key: 'gluten', label: 'Gluten' },
                  { key: 'nuts', label: 'Nuts' }, { key: 'none', label: 'None' },
                ]} />
              </div>
            </div>
          </Card>
        )}

        {/* RESULT: GLÖW Score */}
        {step === STEPS.length && glowResult !== null && (
          <Card className="bg-gradient-to-br from-amber-50 via-white to-orange-50 border border-amber-200/50 rounded-2xl p-8 md:p-10 shadow-lg text-center animate-fade-in" data-testid="glow-result">
            <Sparkles size={32} className="text-amber-500 mx-auto mb-4" />
            <h2 className="font-display text-2xl font-medium mb-2">{de ? 'Dein GLOEW-Score' : 'Your GLOW Score'}</h2>
            <div className="w-32 h-32 rounded-full border-4 border-amber-300 flex items-center justify-center bg-white shadow-lg mx-auto my-6">
              <span className="font-display text-5xl font-bold text-amber-600">{glowResult}</span>
            </div>
            <p className="font-body text-foreground/60 mb-6 max-w-md mx-auto">
              {de
                ? glowResult >= 70 ? 'Super Ausgangslage! Mit belosophie holst du noch mehr raus.' : 'Hier ist Potenzial! belosophie hilft dir, deinen Score zu verbessern.'
                : glowResult >= 70 ? 'Great starting point! With belosophie you can achieve even more.' : 'There is potential! belosophie helps you improve your score.'}
            </p>
            <Button onClick={() => nav('/dashboard')} className="rounded-full bg-terracotta hover:bg-terracotta-dark text-white px-8 py-5 text-lg font-medium shadow-lg shadow-terracotta/20 hover:scale-[1.02] transition-all" data-testid="glow-to-dashboard">
              {de ? 'Zum Dashboard' : 'Go to Dashboard'} <ArrowRight size={20} className="ml-2" />
            </Button>
          </Card>
        )}

        {/* Navigation */}
        {step < STEPS.length && (
          <div className="flex justify-between mt-6">
            {step > 0 ? (
              <Button variant="outline" onClick={() => setStep(step - 1)} className="rounded-full px-6" data-testid="onboarding-back-btn">
                <ArrowLeft size={16} className="mr-2" /> {de ? 'Zurück' : 'Back'}
              </Button>
            ) : <div />}
            {step < STEPS.length - 1 ? (
              <Button onClick={() => setStep(step + 1)} className="rounded-full bg-terracotta hover:bg-terracotta-dark text-white px-6 shadow-lg shadow-terracotta/20" data-testid="onboarding-next-btn">
                {de ? 'Weiter' : 'Next'} <ArrowRight size={16} className="ml-2" />
              </Button>
            ) : (
              <Button onClick={handleFinish} disabled={loading} className="rounded-full bg-terracotta hover:bg-terracotta-dark text-white px-8 shadow-lg shadow-terracotta/20" data-testid="onboarding-finish-btn">
                <Check size={16} className="mr-2" /> {de ? 'GLOEW Score berechnen' : 'Calculate GLOW Score'}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
